/**
 * CampaignForm.jsx
 * Planning a week: what it is about, when it runs, and what it can use.
 *
 * The slot list updates as you change the dates, before anything is generated,
 * so you can see the week you are about to pay to fill.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api.js';
import MediaPicker from './MediaPicker.jsx';
import { Check, Clock, Warning } from './Icons.jsx';
import { formatLondon } from '../utils/time.js';

/** Tomorrow, as YYYY-MM-DD — nothing sensible starts in the past. */
function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function CampaignForm({ brands, available, onPlanned, onCancel }) {
  const [brandId, setBrandId] = useState(brands[0]?.id ?? '');
  const [name, setName] = useState('');
  const [brief, setBrief] = useState('');
  const [goal, setGoal] = useState('');
  const [platforms, setPlatforms] = useState({ facebook: true, instagram: true });
  const [startDate, setStartDate] = useState(tomorrow());
  const [days, setDays] = useState(7);
  const [perDay, setPerDay] = useState(1);
  const [media, setMedia] = useState([]);

  const [slots, setSlots] = useState([]);
  const [slotError, setSlotError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const brandAvailable = available[brandId] ?? { facebook: false, instagram: false };

  // A platform the brand cannot reach is never left ticked.
  useEffect(() => {
    setPlatforms((p) => ({
      facebook: p.facebook && brandAvailable.facebook,
      instagram: p.instagram && brandAvailable.instagram,
    }));
  }, [brandAvailable.facebook, brandAvailable.instagram]);

  // Media belongs to one brand, so switching brand clears the pool.
  useEffect(() => {
    setMedia([]);
  }, [brandId]);

  const preview = useCallback(async () => {
    try {
      const { slots: s } = await api.campaigns.preview({
        start_date: startDate,
        days: Number(days),
        per_day: Number(perDay),
      });
      setSlots(s);
      setSlotError(null);
    } catch (e) {
      setSlots([]);
      setSlotError(e);
    }
  }, [startDate, days, perDay]);

  useEffect(() => {
    preview();
  }, [preview]);

  const chosen = Object.entries(platforms)
    .filter(([, on]) => on)
    .map(([p]) => p);

  const needsMedia = chosen.includes('instagram') && media.length === 0;
  const canPlan =
    brandId && chosen.length > 0 && brief.trim().length >= 10 && slots.length > 0 && !needsMedia;

  async function plan() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.campaigns.plan({
        brand_id: brandId,
        name: name.trim(),
        brief: brief.trim(),
        goal,
        platforms: chosen,
        start_date: startDate,
        days: Number(days),
        per_day: Number(perDay),
        media_ids: media.map((m) => m.id),
      });
      onPlanned(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>Plan a campaign</h2>
          <p>One prompt, a week of drafts. Nothing is scheduled until you approve it.</p>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      <div className="grid">
        <div>
          <div className="field">
            <label htmlFor="c-brand">Brand</label>
            <select id="c-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="c-brief">What is the campaign about?</label>
            <textarea
              id="c-brief"
              rows={5}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="A week introducing PeekoType to people who type on their phone all day: what it does, why the keyboard layout is different, and the two features people notice first."
            />
            <p className="hint">
              The more specific this is, the less the posts repeat each other. Two or three
              sentences is plenty.
            </p>
          </div>

          <div className="field">
            <label htmlFor="c-goal">Goal</label>
            <select id="c-goal" value={goal} onChange={(e) => setGoal(e.target.value)}>
              <option value="">No particular goal</option>
              <option value="awareness">Awareness</option>
              <option value="signups">Signups</option>
              <option value="downloads">Downloads</option>
              <option value="sales">Sales</option>
              <option value="engagement">Engagement</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="c-name">Name (optional)</label>
            <input
              id="c-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Named after the start date if you leave this empty"
            />
          </div>

          <div className="field">
            <label>Publish to</label>
            <div className="btn-row">
              {['facebook', 'instagram'].map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`btn btn--sm ${platforms[p] ? 'btn--primary' : ''}`}
                  disabled={!brandAvailable[p]}
                  title={brandAvailable[p] ? '' : 'No connected account for this brand'}
                  onClick={() => setPlatforms((s) => ({ ...s, [p]: !s[p] }))}
                >
                  {platforms[p] ? <Check size={15} /> : null}
                  {p === 'facebook' ? 'Facebook' : 'Instagram'}
                </button>
              ))}
            </div>
            {!brandAvailable.facebook && !brandAvailable.instagram && (
              <p className="hint">
                This brand has no connected accounts yet. You can still plan the week — it just
                cannot be approved until an account is connected on the Brands screen.
              </p>
            )}
          </div>
        </div>

        <div>
          <div className="field field--row">
            <div>
              <label htmlFor="c-start">Starts</label>
              <input
                id="c-start"
                type="date"
                value={startDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="c-days">Days</label>
              <input
                id="c-days"
                type="number"
                min={1}
                max={14}
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="c-per">Per day</label>
              <input
                id="c-per"
                type="number"
                min={1}
                max={3}
                value={perDay}
                onChange={(e) => setPerDay(e.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label>
              The week
              {slots.length > 0 && <span className="counter">{slots.length} posts</span>}
            </label>

            {slotError ? (
              <div className="notice notice--error">
                <Warning />
                <span>{slotError.message}</span>
              </div>
            ) : (
              <ol className="slots">
                {slots.map((s) => (
                  <li key={s.utc}>
                    <Clock size={14} />
                    {formatLondon(s.utc)}
                  </li>
                ))}
              </ol>
            )}
            <p className="hint">
              Times come from Settings. Slots that have already passed are skipped rather than
              scheduled into the past.
            </p>
          </div>

          <MediaPicker
            brandId={brandId}
            selected={media}
            max={10}
            label="Images and video the campaign can use"
            onChange={setMedia}
          />
          <p className="hint">
            One item per post, chosen to suit what each post says. Add as many as you have posts,
            or fewer and they will be reused.
          </p>
          {needsMedia && (
            <div className="notice notice--error">
              <Warning />
              <span>Instagram needs an image or video. Choose at least one file.</span>
            </div>
          )}
        </div>
      </div>

      <div className="btn-row" style={{ marginTop: 18 }}>
        <button className="btn btn--primary" onClick={plan} disabled={!canPlan || busy}>
          {busy ? 'Writing the week…' : `Plan ${slots.length || ''} posts`}
        </button>
        <button className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {busy && <span className="hint">One request for the whole week. This takes a moment.</span>}
      </div>
    </section>
  );
}
